import React, { useState, useEffect } from 'react';
import { questionnairesAPI } from '../../utils/api';
import toastr from 'toastr';

const QuestionnaireResponseViewer = ({ applicationId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState([]);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [evaluationData, setEvaluationData] = useState({
    evaluationNotes: '',
    autoQualified: false,
    flagged: false,
    flagReason: ''
  });

  useEffect(() => {
    loadResponses();
  }, [applicationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadResponses = async () => {
    try {
      setLoading(true);
      const result = await questionnairesAPI.getApplicationResponses(applicationId);
      setResponses(result.data.responses || []);
      
      if (result.data.responses && result.data.responses.length > 0) {
        const preferredResponse =
          result.data.responses.find((r) => r.status === 'submitted' || r.status === 'evaluated') ||
          result.data.responses[0];
        setSelectedResponse(preferredResponse);
        // Load questionnaire details
        if (preferredResponse.questionnaireId) {
          const qResult = await questionnairesAPI.getById(preferredResponse.questionnaireId._id || preferredResponse.questionnaireId);
          setQuestionnaire(qResult.data.questionnaire);
        }
      }
    } catch (error) {
      console.error('Failed to load responses:', error);
      toastr.error('Failed to load questionnaire responses');
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluate = async () => {
    if (!selectedResponse) return;

    setEvaluating(true);
    try {
      await questionnairesAPI.evaluateResponse(selectedResponse._id, evaluationData);
      toastr.success('Response evaluated successfully');
      loadResponses(); // Reload to get updated data
      setEvaluationData({
        evaluationNotes: '',
        autoQualified: false,
        flagged: false,
        flagReason: ''
      });
    } catch (error) {
      console.error('Failed to evaluate response:', error);
      toastr.error(error.response?.data?.error || 'Failed to evaluate response');
    } finally {
      setEvaluating(false);
    }
  };

  const getQuestionById = (questionId) => { // eslint-disable-line no-unused-vars
    if (!questionnaire?.questions) return null;
    const index = parseInt(questionId);
    if (!isNaN(index) && questionnaire.questions[index]) {
      return questionnaire.questions[index];
    }
    return questionnaire.questions.find(q => q._id?.toString() === questionId) || null;
  };

  const hasAnswerValue = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && value !== '';
  };

  const extractAnswer = (resp) => {
    if (!resp || typeof resp !== 'object') return undefined;
    if (resp.answer !== undefined) return resp.answer;
    // Backward-compatibility for older payload shapes
    if (resp.response !== undefined) return resp.response;
    if (resp.value !== undefined) return resp.value;
    if (resp.text !== undefined) return resp.text;
    if (resp.selectedOption !== undefined) return resp.selectedOption;
    if (resp.selectedOptions !== undefined) return resp.selectedOptions;
    return undefined;
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#6B7280' }}>
        Loading questionnaire responses...
      </div>
    );
  }

  if (responses.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#6B7280', marginBottom: '1rem' }}>
          No questionnaire responses found for this candidate.
        </p>
        <button
          onClick={onClose}
          style={{
            padding: '0.625rem 1.5rem',
            background: '#F3F4F6',
            color: '#374151',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '0.875rem'
          }}
          onMouseEnter={(e) => e.target.style.background = '#E5E7EB'}
          onMouseLeave={(e) => e.target.style.background = '#F3F4F6'}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Response Selector */}
      {responses.length > 1 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
            Select Response
          </label>
          <select
            value={selectedResponse?._id || ''}
            onChange={(e) => {
              const response = responses.find(r => r._id === e.target.value);
              setSelectedResponse(response);
              if (response && response.questionnaireId) {
                questionnairesAPI.getById(response.questionnaireId._id || response.questionnaireId)
                  .then(result => setQuestionnaire(result.data.questionnaire))
                  .catch(err => console.error('Failed to load questionnaire:', err));
              }
              setEvaluationData({
                evaluationNotes: response?.evaluationNotes || '',
                autoQualified: response?.autoQualified || false,
                flagged: response?.flagged || false,
                flagReason: response?.flagReason || ''
              });
            }}
            style={{
              width: '100%',
              padding: '0.625rem 0.875rem',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '0.875rem',
              background: '#ffffff'
            }}
          >
            {responses.map((resp, index) => (
              <option key={resp._id} value={resp._id}>
                {resp.questionnaireId?.title || `Response ${index + 1}`} - {resp.status} - {resp.submittedAt ? new Date(resp.submittedAt).toLocaleDateString() : 'Not submitted'}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedResponse && (
        <>
          {/* Response Summary */}
          <div style={{
            background: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: '6px',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.25rem' }}>Status</div>
                <div style={{ fontWeight: '500', color: '#111827' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    background: selectedResponse.status === 'submitted' ? '#D1FAE5' : 
                                selectedResponse.status === 'evaluated' ? '#DBEAFE' :
                                selectedResponse.status === 'expired' ? '#FEE2E2' : '#F3F4F6',
                    color: selectedResponse.status === 'submitted' ? '#065F46' :
                           selectedResponse.status === 'evaluated' ? '#1E40AF' :
                           selectedResponse.status === 'expired' ? '#991B1B' : '#374151'
                  }}>
                    {selectedResponse.status.charAt(0).toUpperCase() + selectedResponse.status.slice(1)}
                  </span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.25rem' }}>Score</div>
                <div style={{ fontWeight: '600', color: '#111827' }}>
                  {selectedResponse.totalScore || 0} / {selectedResponse.maxPossibleScore || 0}
                  {selectedResponse.maxPossibleScore > 0 && (
                    <span style={{ marginLeft: '0.5rem', color: '#6B7280', fontSize: '0.875rem' }}>
                      ({selectedResponse.percentageScore || 0}%)
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.25rem' }}>Submitted</div>
                <div style={{ fontWeight: '500', color: '#111827' }}>
                  {selectedResponse.submittedAt ? new Date(selectedResponse.submittedAt).toLocaleString() : 'Not submitted'}
                </div>
              </div>
              {selectedResponse.expiresAt && (
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.25rem' }}>Expires</div>
                  <div style={{ fontWeight: '500', color: '#111827' }}>
                    {new Date(selectedResponse.expiresAt).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Responses */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '1rem' }}>
              Candidate Responses
            </h3>
            {(() => {
              const baseResponses = (selectedResponse.responses || [])
                .filter(r => !(r.isExtra || (r.questionId && r.questionId.startsWith('extra_'))))
                .map(r => ({ ...r, answer: extractAnswer(r) }));

              // Source-of-truth: render what candidate actually submitted.
              if (baseResponses.length > 0) {
                return baseResponses.map((resp, index) => {
                  const questionById = questionnaire?.questions?.find(q => String(q._id) === String(resp.questionId));
                  const fallbackQuestion = questionnaire?.questions?.[index];
                  const questionText = resp.question || questionById?.question || fallbackQuestion?.question || `Question ${index + 1}`;
                  const questionType = resp.type || questionById?.type || fallbackQuestion?.type || 'textarea';
                  const questionOptions = questionById?.options || fallbackQuestion?.options || [];

                  return (
                    <div key={`resp-${resp.questionId || index}`} style={{
                      marginBottom: '1.5rem',
                      padding: '1.5rem',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      background: '#FFFFFF'
                    }}>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontWeight: '500', color: '#111827', marginBottom: '0.25rem' }}>
                          {index + 1}. {questionText}
                        </div>
                        {resp.score !== undefined && (
                          <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                            Score: {resp.score}
                          </div>
                        )}
                      </div>

                      <div style={{
                        padding: '0.875rem',
                        background: '#F9FAFB',
                        borderRadius: '4px',
                        border: '1px solid #E5E7EB'
                      }}>
                        {!hasAnswerValue(resp.answer) ? (
                          <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No answer provided</span>
                        ) : questionType === 'multiple_choice' ? (
                          <div style={{ color: '#374151' }}>
                            {questionOptions.find(opt => String(opt.value) === String(resp.answer))?.label || resp.answer}
                          </div>
                        ) : questionType === 'checkbox' ? (
                          <div style={{ color: '#374151' }}>
                            {Array.isArray(resp.answer) ? (
                              <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                                {resp.answer.map((val, i) => (
                                  <li key={i} style={{ marginBottom: '0.25rem' }}>
                                    {questionOptions.find(opt => String(opt.value) === String(val))?.label || val}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              resp.answer
                            )}
                          </div>
                        ) : questionType === 'yes_no' ? (
                          <div style={{ color: '#374151', fontWeight: '500' }}>
                            {String(resp.answer).toLowerCase() === 'yes' ? 'Yes' : 'No'}
                          </div>
                        ) : questionType === 'scale' ? (
                          <div style={{ color: '#374151', fontSize: '1.25rem', fontWeight: '600' }}>
                            {resp.answer}
                          </div>
                        ) : (
                          <div style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>
                            {resp.answer}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                });
              }

              return <p style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No questionnaire answers were stored for this submission.</p>;
            })()}

            {/* Extra (per-candidate) questions */}
            {(() => {
              const extraResponses = (selectedResponse.responses || [])
                .filter(r => r.isExtra || (r.questionId && r.questionId.startsWith('extra_')))
                .map(r => ({ ...r, answer: extractAnswer(r) }));
              if (extraResponses.length === 0) return null;
              return (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    margin: '1.5rem 0 1rem',
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                    color: '#92400E',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    <span style={{ width: '1.25rem', height: '1.25rem', borderRadius: '50%', background: '#FDE68A', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.625rem' }}>+</span>
                    Custom Questions for This Candidate
                  </div>
                  {extraResponses.map((resp, idx) => (
                    <div key={`extra-${idx}`} style={{
                      marginBottom: '1.5rem',
                      padding: '1.5rem',
                      border: '1px solid #FDE68A',
                      borderRadius: '6px',
                      background: '#FFFBEB'
                    }}>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontWeight: '500', color: '#92400E', marginBottom: '0.25rem' }}>
                          {(questionnaire?.questions?.length || 0) + idx + 1}. {resp.question || 'Custom Question'}
                        </div>
                      </div>
                      <div style={{
                        padding: '0.875rem',
                        background: '#FFFFFF',
                        borderRadius: '4px',
                        border: '1px solid #FDE68A'
                      }}>
                        {!hasAnswerValue(resp.answer) ? (
                          <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No answer provided</span>
                        ) : Array.isArray(resp.answer) ? (
                          <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#374151' }}>
                            {resp.answer.map((val, i) => (
                              <li key={i} style={{ marginBottom: '0.25rem' }}>{val}</li>
                            ))}
                          </ul>
                        ) : resp.type === 'yes_no' ? (
                          <div style={{ color: '#374151', fontWeight: '500' }}>{resp.answer === 'yes' ? 'Yes' : 'No'}</div>
                        ) : (
                          <div style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>{resp.answer}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              );
            })()}
          </div>

          {/* Evaluation Section */}
          {selectedResponse.status === 'submitted' && (
            <div style={{
              borderTop: '2px solid #E5E7EB',
              paddingTop: '1.5rem'
            }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '1rem' }}>
                Evaluate Response
              </h3>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={evaluationData.autoQualified}
                    onChange={(e) => setEvaluationData({ ...evaluationData, autoQualified: e.target.checked })}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '500', color: '#374151' }}>Auto-qualify candidate</span>
                </label>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={evaluationData.flagged}
                    onChange={(e) => setEvaluationData({ ...evaluationData, flagged: e.target.checked, flagReason: e.target.checked ? evaluationData.flagReason : '' })}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '500', color: '#374151' }}>Flag for review</span>
                </label>
              </div>

              {evaluationData.flagged && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                    Flag Reason
                  </label>
                  <textarea
                    value={evaluationData.flagReason}
                    onChange={(e) => setEvaluationData({ ...evaluationData, flagReason: e.target.value })}
                    placeholder="Why is this response flagged?"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.875rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
              )}

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                  Evaluation Notes
                </label>
                <textarea
                  value={evaluationData.evaluationNotes}
                  onChange={(e) => setEvaluationData({ ...evaluationData, evaluationNotes: e.target.value })}
                  placeholder="Add your evaluation notes..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.875rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button
                  onClick={onClose}
                  disabled={evaluating}
                  style={{
                    padding: '0.625rem 1.5rem',
                    background: '#F3F4F6',
                    color: '#374151',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    cursor: evaluating ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                  onMouseEnter={(e) => !evaluating && (e.target.style.background = '#E5E7EB')}
                  onMouseLeave={(e) => !evaluating && (e.target.style.background = '#F3F4F6')}
                >
                  Close
                </button>
                <button
                  onClick={handleEvaluate}
                  disabled={evaluating}
                  style={{
                    padding: '0.625rem 1.5rem',
                    background: evaluating ? '#9CA3AF' : '#1E40AF',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: evaluating ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                  onMouseEnter={(e) => !evaluating && (e.target.style.background = evaluating ? '#9CA3AF' : '#1E3A8A')}
                  onMouseLeave={(e) => !evaluating && (e.target.style.background = evaluating ? '#9CA3AF' : '#1E40AF')}
                >
                  {evaluating ? 'Evaluating...' : 'Submit Evaluation'}
                </button>
              </div>
            </div>
          )}

          {/* Already Evaluated */}
          {selectedResponse.status === 'evaluated' && (
            <div style={{
              borderTop: '2px solid #E5E7EB',
              paddingTop: '1.5rem'
            }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '1rem' }}>
                Evaluation Details
              </h3>
              
              {selectedResponse.autoQualified && (
                <div style={{
                  padding: '0.75rem',
                  background: '#D1FAE5',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  border: '1px solid #10B981'
                }}>
                  <div style={{ fontWeight: '600', color: '#065F46', marginBottom: '0.25rem' }}>
                    ✓ Auto-qualified
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#047857' }}>
                    Candidate automatically qualified based on questionnaire responses
                  </div>
                </div>
              )}

              {selectedResponse.flagged && (
                <div style={{
                  padding: '0.75rem',
                  background: '#FEE2E2',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  border: '1px solid #EF4444'
                }}>
                  <div style={{ fontWeight: '600', color: '#991B1B', marginBottom: '0.25rem' }}>
                    ⚠ Flagged for Review
                  </div>
                  {selectedResponse.flagReason && (
                    <div style={{ fontSize: '0.875rem', color: '#B91C1C', marginTop: '0.25rem' }}>
                      {selectedResponse.flagReason}
                    </div>
                  )}
                </div>
              )}

              {selectedResponse.evaluationNotes && (
                <div style={{
                  padding: '0.875rem',
                  background: '#F9FAFB',
                  borderRadius: '6px',
                  border: '1px solid #E5E7EB',
                  marginBottom: '1rem'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.5rem' }}>
                    Evaluation Notes
                  </div>
                  <div style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {selectedResponse.evaluationNotes}
                  </div>
                </div>
              )}

              {selectedResponse.evaluatedBy && (
                <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  Evaluated by {selectedResponse.evaluatedBy.fullName || selectedResponse.evaluatedBy.email} on{' '}
                  {selectedResponse.evaluatedAt ? new Date(selectedResponse.evaluatedAt).toLocaleString() : 'N/A'}
                </div>
              )}

              <div style={{ marginTop: '1.5rem' }}>
                <button
                  onClick={onClose}
                  style={{
                    padding: '0.625rem 1.5rem',
                    background: '#F3F4F6',
                    color: '#374151',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#E5E7EB'}
                  onMouseLeave={(e) => e.target.style.background = '#F3F4F6'}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default QuestionnaireResponseViewer;


